import { clsx } from 'clsx'
import { isBoolean, isEmpty, isObject, isPlainObject, isString, some } from 'lodash'
// eslint-disable-next-line no-restricted-imports
import { toast as displayToast } from 'sonner'
import { twMerge } from 'tailwind-merge'

// Component
import {
  oneUppercaseLetterRegEx,
  oneLowercaseLetterRegEx,
  oneNumberRegEx,
  oneSymbolRegEx,
} from '../components/PasswordVerifier'

const ERROR_DEFAULT = 'An error occurred. Please try again.'
const ERROR_NO_INTERNET = 'Request could not be made. Please check your internet connection.'
const ERROR_TIMEOUT = 'Request timed out. Please try again.'

export const createWithDoc = ({ envName = '', docFunction = () => {}, component = '' }) => {
  let createComponentWithDoc
  if (envName !== 'production') {
    createComponentWithDoc = docFunction(component) // eslint-disable-line global-require
  }
  return createComponentWithDoc || component
}

export const getErrorMessage = (err) => {
  // - Axios does not return a response object if the service cannot be reached
  if (err.code === 'ERR_NETWORK') {
    return ERROR_NO_INTERNET
  }

  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
    return ERROR_TIMEOUT
  }

  // - If we do have a response and data object, use the full error object
  // Error responses come in a variety of formats, so we need to handle them carefully.
  // Example error responses:
  // --- "A simple message"
  // --- {"__all__":["Entity role with this User and Entity already exists."]}
  // --- {
  //     "entity_roles": [
  //         {
  //             "non_field_errors": [
  //                 "The fields entity, role must make a unique set."
  //             ]
  //         }
  //     ]
  // }
  // --- {
  //     "entity_roles": [
  //         {},
  //         {
  //             "non_field_errors": [
  //                 "The fields entity, role must make a unique set."
  //             ]
  //         }
  //     ]
  // }
  if (err.response && err.response.data) {
    try {
      // TODO: this is a bit of a hack and deserves a better solution.
      const errorStringOrObject = Object.values(err.response.data)[0]
      if (isObject(errorStringOrObject)) {
        if (isObject(errorStringOrObject[0])) {
          const nestedErrorStringOrObject = Object.values(errorStringOrObject[0])[0]
          return nestedErrorStringOrObject[0]
        }
        return errorStringOrObject[0]
      }
      return errorStringOrObject
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Could not infer error message from error response object')
    }
  }

  // If a message has not been set, fallback to our default message
  return ERROR_DEFAULT
}

/**
 * Updates the state object with the updated field.
 * @param {object} state
 * @param {object} updatedField
 */
export const updateState = (state, updatedField) => ({
  ...state,
  ...updatedField,
})

/**
 * Updates an array stored in state.
 * - `add`: adds value to the array
 * - `remote`: removes the value at the index from the array
 * - `update`: updates the value at the index in the array
 * - `reset` and `filter`: returns the supplied value
 * - `clear`: removes all values from the array
 * @param {object} state
 * @param {object} updatedState
 */
export const updateArrayState = (state, { type, value, index }) => {
  switch (type) {
    case 'add':
      return [...state, value]
    case 'remove': {
      const updatedState = [...state]
      updatedState.splice(index, 1)
      return updatedState
    }
    case 'update': {
      const updatedState = [...state]
      updatedState[index] = value
      return updatedState
    }
    case 'reset':
    case 'filter':
      return value
    case 'clear':
      return []
    default:
      return state
  }
}

export const joinClassNames = (...classes) => classes.filter(Boolean).join(' ')

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export const toast = (message, type) => {
  if (type === 'error') {
    displayToast.error(message)
  } else {
    displayToast.success(message)
  }
}

/**
 * Handles updating pagination based on the supplied parameters and functions.
 * @param {number} page
 * @param {number} currentPage
 * @param {number} perPage
 * @param {number} totalRows
 * @param {object} pages
 * @param {func} setCurrentPage
 * @param {func} request
 * @param {string} baseUrl
 * @param {string} filter
 */
export const handlePagination = async (
  page,
  currentPage,
  perPage,
  totalRows,
  pages,
  setCurrentPage,
  request,
  baseUrl,
  filter = null,
) => {
  // If the user is requesting the first page and we are not on the next page,
  // we need to get the very first page and not utilize `previous`.
  if (page === 1 && currentPage > 1) {
    await request(`${baseUrl}${perPage}&${filter ? `${filter}&` : ''}page=1`)
  }
  // If the user is requesting the last page and we are not on the previous page,
  // we need to get the very last page and not utilize `next`.
  else if (page > currentPage && page - currentPage > 1) {
    await request(
      `${baseUrl}${perPage}&${filter ? `${filter}&` : ''}page=${Math.ceil(totalRows / perPage)}`,
    )
  }
  // If the user is requesting the next page.
  else if (page > currentPage) {
    await request(pages.next)
  }
  // Otherwise, the user is requesting the previous page.
  else {
    await request(pages.previous)
  }

  setCurrentPage(page)
}

/**
 * Handles verifying the password against requirements.
 * @param {string} value
 * @returns {string} error if there is one to display
 */
export const verifyPassword = (value) => {
  if (!value || value.length < 8) return 'Please enter more than 8 characters'
  if (!oneUppercaseLetterRegEx.test(value)) return 'Please enter at least 1 uppercase letter'
  if (!oneLowercaseLetterRegEx.test(value)) return 'Please enter at least 1 lowercase letter'
  if (!oneNumberRegEx.test(value)) return 'Please enter at least 1 number'
  if (!oneSymbolRegEx.test(value)) return 'Please enter at least 1 symbol'
  return undefined
}

/**
 * Determines if any filter is selected in the filters object.
 * @param {object} filters
 */
export const hasAnyFilterSelected = (filters) =>
  some(filters, (filter) => Array.isArray(filter) && !isEmpty(filter))

/**
 * Determines if any value in the object is not empty
 * @param {object} obj
 * @returns {boolean}
 */
export const hasAnyValues = (obj) =>
  isPlainObject(obj) && Object.values(obj).some((val) => !isEmpty(val))
/**
 * Generates query string based on updated filter structure
 * @param {object} filters
 */
export const configureFilterQuery = (filters) => {
  const filterObj = []
  Object.entries(filters).forEach(([key, value]) => {
    // String and boolean values
    if ((isString(value) && value !== '') || isBoolean(value)) {
      filterObj.push([key, value])
    }
    // Nested object values
    else if (isPlainObject(value)) {
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (isString(subValue) || isBoolean(subValue)) {
          filterObj.push([`${key}_${subKey}`, subValue])
        }
        if (Array.isArray(subValue) && !isEmpty(subValue)) {
          subValue.forEach((arrayValue) => {
            filterObj.push([`${key}_${subKey}`, arrayValue])
          })
        }
      })
    } // Array values
    else if (Array.isArray(value) && !isEmpty(value)) {
      value.forEach((arrayValue) => {
        filterObj.push([key, arrayValue])
      })
    }
  })
  return filterObj
}

/**
 * @typedef SortingParam
 * @property {string} id - The column ID to sort by. This should match
 * the API field name, so it should be snake case and not camel case.
 * @property {boolean} desc - Whether the sort is descending
 */

/**
 * Generates the sort parameter. While this is an array, we only support sorting by one column at a time.
 * @param {SortingParam[]} sorting
 * @return {string}
 */
export const configureSortParam = (sorting) => {
  if (!sorting || sorting.length === 0) return {}

  // Sort by the first column in the sorting array
  const sorts = sorting.map(({ id, desc }) => `${desc ? '-' : ''}${id}`)

  return { order_by: sorts.join(',') }
}

/**
 * Configures the pagination parameters for the request.
 * @param {object} pagination
 */
export const configurePaginationParam = (pagination) => {
  if (!pagination || isEmpty(pagination)) return {}
  return { page: pagination.pageIndex + 1, limit: pagination.pageSize }
}

export const configureRequestParams = (
  sorting = [],
  pagination = {},
  searchTerm = null,
  filters = {},
) => {
  const sort = configureSortParam(sorting)
  const paginationParam = configurePaginationParam(pagination)
  const filterQuery = configureFilterQuery(filters)
  const params = new URLSearchParams([
    ...Object.entries(paginationParam),
    ...(searchTerm ? [['q', searchTerm]] : []),
    ...filterQuery,
    ...Object.entries(sort),
  ])

  return `?${params.toString()}`
}

/**
 * Returns a clone of the array, with the items in random order
 * @template T
 * @param {T[]} array
 * @return {T[]}
 */
export const randomizeArray = (array) => {
  const copiedArray = structuredClone(array)
  const newArray = new Array(copiedArray.length).fill(null).map(() => {
    const randomIndex = Math.floor(Math.random() * copiedArray.length)
    const val = copiedArray.splice(randomIndex, 1)
    return val[0]
  })
  return newArray
}

/**
 * Formats a number as USD currency
 * @param {number|string} amount - The amount to format
 * @return {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numAmount)
}

/**
 * Formats a date/datetime string
 * @param {string} dateString - The date string to format
 * @return {string} Formatted date string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/**
 * @typedef RequestParams
 * @property {SortingParam} [sorting] - Sorting configuration.
 * @property {object} [pagination] - Pagination configuration.
 * @property {string} [searchTerm] - Search term for filtering results.
 * @property {object} [filters] - Additional filters for the request.
 */

/**
 * Unique, present email addresses in first-seen order. Rows that map to the
 * same person (e.g. one student certificate per course registration) would
 * otherwise duplicate their email in copy-to-clipboard lists.
 * @param {(string|null|undefined)[]} emails
 * @returns {string[]}
 */
export const uniqueEmails = (emails) => [...new Set(emails.filter(Boolean))]
